import { useEffect, useState } from "react";

export const PRODUCT_HUNT_LAUNCH_START = Date.parse("2026-09-18T07:00:00Z");
export const PRODUCT_HUNT_LAUNCH_END = Date.parse("2026-09-19T07:00:00Z");

const PRODUCT_HUNT_URL =
  "https://www.producthunt.com/products/scroll-6?utm_source=scroll&utm_medium=launch_banner&utm_campaign=scroll_2_0";

type LaunchPhase = "countdown" | "live" | "expired";

export function getProductHuntLaunchPhase(now: number): LaunchPhase {
  if (now < PRODUCT_HUNT_LAUNCH_START) return "countdown";
  if (now < PRODUCT_HUNT_LAUNCH_END) return "live";
  return "expired";
}

export function formatProductHuntCountdown(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [hours, minutes, seconds].map((value) => String(value).padStart(2, "0")).join(":");
}

interface ProductHuntLaunchBannerProps {
  onExpired: () => void;
}

export default function ProductHuntLaunchBanner({ onExpired }: ProductHuntLaunchBannerProps) {
  const [now, setNow] = useState(() => Date.now());
  const phase = getProductHuntLaunchPhase(now);

  useEffect(() => {
    if (phase === "expired") {
      onExpired();
      return;
    }

    const intervalId = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [onExpired, phase]);

  if (phase === "expired") return null;

  const countdown = formatProductHuntCountdown(PRODUCT_HUNT_LAUNCH_START - now);
  const isLive = phase === "live";

  return (
    <aside
      className="product-hunt-launch-banner"
      aria-label={isLive ? "Scroll 2.0 is live on Product Hunt today" : "Scroll 2.0 Product Hunt launch countdown"}
      data-phase={phase}
    >
      <a href={PRODUCT_HUNT_URL} target="_blank" rel="noopener noreferrer">
        <span className="product-hunt-launch-badge" aria-hidden="true">
          <span className="product-hunt-launch-mark">P</span>
        </span>

        {isLive ? (
          <span className="product-hunt-launch-message">
            Scroll 2.0 is live <span className="product-hunt-launch-platform">on Product Hunt</span> today.
          </span>
        ) : (
          <span className="product-hunt-launch-message">
            Scroll 2.0 launches <span className="product-hunt-launch-platform">on Product Hunt </span>in
          </span>
        )}

        {!isLive && (
          <time
            className="product-hunt-launch-countdown"
            dateTime="2026-09-18T00:00:00-07:00"
            aria-label={`${countdown} until launch`}
          >
            {countdown}
          </time>
        )}

        <span className="product-hunt-launch-cta">
          <span className="product-hunt-launch-cta-long">{isLive ? "See the launch" : "View on Product Hunt"}</span>
          <span className="product-hunt-launch-cta-short">View</span>
          <span aria-hidden="true"> ↗</span>
        </span>
      </a>
    </aside>
  );
}
